import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Tooltip } from '@fluentui/react-components';
import { FluentTooltip } from '../../../../components/FluentUI/Fluent-Tooltip';
import { FluentButton } from '../../../../components/FluentUI/Fluent-Button';
import FluentUiIcon from '../../../../assets/fluent-ui-icon';
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { renderTooltipHeader } from '../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper';
import DealTeamAvatarGroupCell from '../../dashboard-util/dashboard-telerik-helper/deal-team-avatar';
import { getAccordionMemberRowRender, getBankerInfoFlattenedData, getFilteredSortedData, getMyDealModalDetailColumns,  handleDealTrackerModalExport, LevfinBankerEmptyState, LevfinCapacity, ProjectIdCell, useMemberGroupToggle } from '../levfin-helper';
import { FluentAvatar } from '../../../../components/FluentUI/Fluent-Avatar';
import {
    createColumnMenu,
} from "../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { ExcelExport } from '@progress/kendo-react-excel-export';
import PropTypes from "prop-types";

const LevfinDealTrackerModal = ({
    show,
    onHide,
    deal,
    categoryOptions
}) => {
    const _detailExport = useRef(null);
    const [flattenedData, setFlattenedData] = useState(deal.BankerInfo);
    const [memberState, setMemberState] = useState({ sort: [], filter: null });
    const columnMenu = useMemo(() => createColumnMenu(flattenedData), [flattenedData]);
    const columnIsFiltered = useCallback(
        (fieldName) => isFilteredColumn(fieldName, memberState.filter),
        [memberState.filter]
    );
    const filteredData = useMemo(() => getFilteredSortedData(flattenedData, memberState.filter, memberState.sort, categoryOptions), [flattenedData, memberState.filter, memberState.sort, categoryOptions]);
    const { groupState, toggleGroup, setGroupState } = useMemberGroupToggle();
    const AccordionRowRender = useMemo(() => getAccordionMemberRowRender(filteredData, groupState, toggleGroup, 1, "brand"), [filteredData, groupState, memberState.filter, categoryOptions]);
    useEffect(() => {
        document.body.classList.toggle("energy-dialog-open", show);
    }, [show]);
    return (
        <>
            <Dialog open={show} modalType="modal" data-testid="deal-resource-modal"
                onOpenChange={(event, data) => {
                    if (!data.open) {
                        onHide();
                    }
                }}>
                <DialogSurface className="power-member-tracker-modal deal-resource-modal levfin-time-log">
                    <DialogBody>
                        <DialogTitle className="d-flex justify-content-between "
                            action={
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="subtle"
                                        aria-label="close"
                                        icon={<FluentUiIcon name="DismissRegular" />}
                                    />
                                </DialogTrigger>
                            }
                        >

                            <span>
                                <ProjectIdCell
                                    dataItem={deal}
                                    canEdit={false}
                                    categoryOptions={categoryOptions}
                                />
                                {deal?.DealName}
                            </span>
                            <div className="d-flex align-items-start justify-content-end">
                                <FluentTooltip content="Export Data" target={
                                    <FluentButton
                                        appearance="secondaryWithOutline"
                                        icon={
                                            <FluentUiIcon
                                                name="ArrowDownloadRegular"
                                                color="#0F6CBD"
                                                size={20}
                                            />
                                        }
                                        onClick={() =>
                                            handleDealTrackerModalExport({ detailRef: _detailExport, deal })
                                        }
                                    />
                                }>
                                </FluentTooltip>
                            </div>
                        </DialogTitle>

                        <DialogContent>
                            <div className="detail-grid-scroll-container project-staffer-member-modal">
                                <Grid
                                    data={[deal]}
                                    dataItemKey={"Banker"}
                                    className='member-grid-summary'
                                >
                                    <Column
                                        field="GroupName"
                                        title={renderTooltipHeader("Category")}
                                        cell={(props) =>
                                        (
                                            <td className="background-white k-table-td">
                                                <Tooltip content={props.dataItem.GroupName} relationship="label">
                                                    <span>{props.dataItem.GroupName}</span>
                                                </Tooltip>
                                            </td>
                                        )
                                        }
                                    />
                                    <Column
                                        field="ProcessType"
                                        title={renderTooltipHeader("Process Type")}
                                        cell={(props) =>
                                        (
                                            <td className="background-white k-table-td">
                                                <Tooltip content={props.dataItem.ProcessType} relationship="label">
                                                        <span>
                                                            {props.dataItem.ProcessType}
                                                        </span>
                                                </Tooltip>
                                            </td>
                                        )
                                        }
                                    />
                                    <Column
                                        field="Industry"
                                        title={renderTooltipHeader("Industry")}
                                        cell={(props) =>
                                        (
                                            <td className="background-white k-table-td">
                                                <Tooltip content={props.dataItem.Industry} relationship="label">
                                                    <span>{props.dataItem.Industry}</span>
                                                </Tooltip>
                                            </td>
                                        )
                                        }
                                    />
                                    <Column
                                        field="Deliverable"
                                        title={renderTooltipHeader("Deliverable")}
                                        cell={(props) =>
                                        (
                                            <td className="background-white k-table-td">
                                                <Tooltip content={props.dataItem.Deliverable} relationship="label">
                                                    <span>{props.dataItem.Deliverable}</span>
                                                </Tooltip>
                                            </td>
                                        )
                                        }
                                    />
                                    <Column
                                        field="TimeCommitment"
                                        title={renderTooltipHeader("Deal Time Commitment")}
                                        cell={(props) =>
                                        (
                                            <td className="background-white k-table-td">
                                                <LevfinCapacity capacity={props.dataItem.TimeCommitment} />
                                            </td>
                                        )
                                        }
                                    />
                                    <Column
                                        field="MD_D"
                                        title={renderTooltipHeader("MD / D")}
                                        cell={(props) =>
                                        (
                                            <td className="background-white k-table-td avatar-cell">
                                                <Tooltip content={props.dataItem.MD_D} relationship="label">
                                                    <div className="avatar-content">
                                                        <FluentAvatar name={props.dataItem.MD_D} color="cornflower" size={24} />
                                                        <span>{props.dataItem.MD_D}</span>
                                                    </div>
                                                </Tooltip>
                                            </td>
                                        )
                                        }
                                    />
                                    <Column
                                        field="D_VP"
                                        title={renderTooltipHeader("D / VP")}
                                        cell={(props) =>
                                        (
                                            <td className="background-white k-table-td avatar-cell">
                                                <Tooltip content={props.dataItem.D_VP} relationship="label">
                                                    <div className="avatar-content">
                                                        <FluentAvatar name={props.dataItem.D_VP} color="pink" size={24} />
                                                        <span>{props.dataItem.D_VP}</span>
                                                    </div>
                                                </Tooltip>
                                            </td>
                                        )
                                        }
                                    />
                                    <Column
                                        field="Staffers"
                                        title={renderTooltipHeader("Staffer(s)")}
                                        cell={(props) =>
                                        (
                                            <td className="background-white k-table-td">
                                                {props.dataItem.Staffers?.length
                                                    ? <DealTeamAvatarGroupCell members={props.dataItem.Staffers} />
                                                    : "-"
                                                }
                                            </td>
                                        )
                                        }
                                    />
                                </Grid>
                                {!filteredData || filteredData.length === 0 ? (
                                    <div className="no-data-state">
                                        <LevfinBankerEmptyState CanEdit={deal.CanEdit} DealId={deal.DealId} />
                                    </div>
                                ) : <Grid
                                    data={filteredData}
                                    dataItemKey={"ProjectId"}
                                    sortable={true}
                                    sort={memberState.sort}
                                    filter={memberState.filter}
                                    onSortChange={(e) => {
                                        setMemberState({ ...memberState, sort: e.sort })
                                        setGroupState({ ...groupState });
                                    }}
                                    onFilterChange={(e) => {
                                        setMemberState({ ...memberState, filter: e.filter })
                                    }}
                                    rowRender={AccordionRowRender}
                                    resizable={true}
                                >
                                    <Column
                                        field="Banker"
                                        title={renderTooltipHeader("Banker")}
                                        width={150}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("Banker") ? "filtered" : ""}
                                        cell={(props) => (
                                            <td className="summary-cell k-table-td avatar-cell">
                                                <div className="avatar-content">
                                                    <FluentAvatar name={props.dataItem.Banker} color="brand" size={24} />
                                                    <span>{props.dataItem.Banker}</span>
                                                </div>
                                            </td>

                                        )}
                                    />
                                </Grid>}

                            </div>
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <ExcelExport data={getBankerInfoFlattenedData([deal])} ref={_detailExport}>
                {getMyDealModalDetailColumns([deal])}
            </ExcelExport>
        </>
    );
};

LevfinDealTrackerModal.propTypes = {
    deal: PropTypes.shape({
        ProjectId: PropTypes.string,
        DealId: PropTypes.string,
        Banker: PropTypes.string,
        DealTeam: PropTypes.arrayOf(
            PropTypes.shape({
                Key: PropTypes.string,
                Value: PropTypes.number,
            })
        ),
        Total: PropTypes.number,
    }),
};

export default LevfinDealTrackerModal;
